import React, { useState } from 'react';
import { 
  Home, 
  Users, 
  ShoppingCart, 
  Package, 
  BarChart3, 
  Settings,
  LogOut,
  TrendingUp,
  Truck,
  PhoneCall,
  Plane,
  MessageCircle,
  Inbox,
  FileText,
  Target,
  Zap,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Store,
  Search
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const SidebarNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [expandedSections, setExpandedSections] = useState({
    sales: true,
    orders: true,
    products: false,
    customers: false,
    marketing: false,
    analytics: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const menuSections = [
    {
      id: 'sales',
      title: 'Sales',
      icon: Home,
      items: [
        { icon: Home, label: 'Dashboard', path: '/dashboard' },
        { icon: DollarSign, label: 'Pricing & Offers', path: '/pricing-dashboard' },
        { icon: Target, label: 'Marketing', path: '/marketing' },
      ]
    },
    {
      id: 'orders',
      title: 'Orders',
      icon: ShoppingCart,
      items: [
        { icon: ShoppingCart, label: 'All Orders', path: '/orders' },
        { icon: PhoneCall, label: 'Confirmations', path: '/confirmation' },
        { icon: Truck, label: 'Dispatch', path: '/tracker' },
        { icon: Plane, label: 'China Purchases', path: '/purchase' },
        { icon: Store, label: 'Supplier Hub', path: '/supplier-hub' },
      ]
    },
    {
      id: 'products',
      title: 'Products',
      icon: Package,
      items: [
        { icon: Package, label: 'Inventory', path: '/inventory' },
        { icon: BarChart3, label: 'Overview', path: '/inventory-overview' },
        { icon: TrendingUp, label: 'Import & Analytics', path: '/inventory-import' },
        { icon: Package, label: 'DTDC Management', path: '/dtdc-management' },
      ]
    },
    {
      id: 'customers',
      title: 'Customers',
      icon: Users,
      items: [
        { icon: Users, label: 'All Customers', path: '/customers' },
        { icon: Users, label: 'Segments', path: '/segments' },
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing',
      icon: Target,
      items: [
        { icon: Target, label: 'Campaigns', path: '/campaigns' },
        { icon: Zap, label: 'Flash Sales', path: '/flash-sales' },
        { icon: Package, label: 'Bundles', path: '/bundles' },
        { icon: MessageCircle, label: 'WhatsApp', path: '/whatsapp' },
        { icon: Inbox, label: 'Inbox', path: '/whatsapp-inbox' },
        { icon: FileText, label: 'Templates', path: '/whatsapp-templates' },
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics',
      icon: BarChart3,
      items: [
        { icon: BarChart3, label: 'Reports', path: '/reports' },
        { icon: TrendingUp, label: 'Analytics', path: '/analytics' },
      ]
    }
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="w-60 bg-[#f7f8fa] border-r border-[#e1e3e5] min-h-screen flex flex-col">
      {/* Logo & Store Name */}
      <div className="px-3 py-4 border-b border-[#e1e3e5] bg-white">
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#f7f8fa] cursor-pointer transition-all duration-200">
          <div className="w-8 h-8 bg-gradient-to-br from-[#008060] to-[#005e46] rounded-lg flex items-center justify-center shadow-sm">
            <Store className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-sm font-semibold text-[#202223]">TNC Collection</h1>
            <p className="text-xs text-[#6d7175]">Store</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-3 border-b border-[#e1e3e5] bg-white">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6d7175]" />
          <input
            type="text"
            placeholder="Search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#c9cccf] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005e46] focus:border-transparent bg-white text-[#202223] placeholder-[#6d7175]"
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto">
        {menuSections.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded = expandedSections[section.id];
          
          return (
            <div key={section.id} className="mb-1">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#e3e5e7] transition-all duration-200 group"
              >
                <div className="flex items-center gap-3">
                  <SectionIcon className="w-5 h-5 text-[#6d7175] group-hover:text-[#303030]" />
                  <span className="text-sm font-medium text-[#303030]">{section.title}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-[#6d7175]" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[#6d7175]" />
                )}
              </button>

              {/* Section Items */}
              {isExpanded && (
                <div className="ml-3 mt-1 space-y-0.5">
                  {section.items.map((item) => {
                    const ItemIcon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                          transition-all duration-200 text-left
                          ${isActive 
                            ? 'bg-[#f1f2f4] text-[#202223] font-medium' 
                            : 'text-[#303030] hover:bg-[#e3e5e7]'
                          }
                        `}
                      >
                        <ItemIcon className={`w-4 h-4 ${isActive ? 'text-[#008060]' : 'text-[#6d7175]'}`} />
                        <span className="text-sm">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Settings & Logout */}
      <div className="border-t border-[#e1e3e5] p-3 space-y-1 bg-white">
        <button
          onClick={() => navigate('/settings')}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
            transition-all duration-200 text-left
            ${location.pathname === '/settings'
              ? 'bg-[#f1f2f4] text-[#202223] font-medium' 
              : 'text-[#303030] hover:bg-[#e3e5e7]'
            }
          `}
        >
          <Settings className={`w-4 h-4 ${location.pathname === '/settings' ? 'text-[#008060]' : 'text-[#6d7175]'}`} />
          <span className="text-sm">Settings</span>
        </button>
        
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[#303030] hover:bg-[#e3e5e7] transition-all duration-200 text-left"
        >
          <LogOut className="w-4 h-4 text-[#6d7175]" />
          <span className="text-sm">Log out</span>
        </button>
      </div>
    </div>
  );
};

export default SidebarNew;
